import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiAward,FiUsers,FiHeart,FiTarget,FiStar,FiTrendingUp}=FiIcons;

const About=()=> {
  const values=[
    {icon: FiAward,title: 'Excellence',description: 'We strive for perfection in every piece we create,using only the finest materials and techniques.'},
    {icon: FiHeart,title: 'Passion',description: 'Our love for jewelry design drives us to create pieces that capture emotions and memories.'},
    {icon: FiUsers,title: 'Service',description: 'Customer satisfaction is our priority,providing personalized service and expert guidance.'},
    {icon: FiTarget,title: 'Innovation',description: 'We blend traditional craftsmanship with modern techniques to create timeless designs.'}
  ];

  const team=[
    {name: 'Anna Rodriguez',role: 'Master Jeweler & Founder',image: 'https://images.unsplash.com/photo-1494790108755-2616b612b5c4?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=687&q=80',description: 'With over 25 years of experience,Anna founded Anni Jewelry with a vision to create exceptional pieces.'},
    {name: 'Michael Chen',role: 'Design Director',image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',description: 'Michael brings innovative design concepts while maintaining the elegance of traditional jewelry.'},
    {name: 'Sarah Thompson',role: 'Customer Relations Manager',image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80',description: 'Sarah ensures every customer receives personalized attention and exceptional service experience.'}
  ];

  const achievements=[
    {icon: FiStar,number: '25+',label: 'Years of Excellence'},
    {icon: FiAward,number: '15+',label: 'Industry Awards'},
    {icon: FiUsers,number: '5000+',label: 'Happy Customers'},
    {icon: FiTrendingUp,number: '99%',label: 'Satisfaction Rate'}
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-black via-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{opacity: 0,y: 30}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            className="text-5xl md:text-6xl font-bold text-white mb-6 font-playfair"
          >
            About <span className="text-gold">Anni Jewelry</span>
          </motion.h1>
          <motion.p
            initial={{opacity: 0,y: 30}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.8,delay: 0.2}}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            For over two decades,we have been crafting exceptional jewelry pieces that celebrate life's most precious moments with unparalleled artistry and dedication.
          </motion.p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0,x: -30}}
              whileInView={{opacity: 1,x: 0}}
              transition={{duration: 0.8}}
              viewport={{once: true}}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-playfair">
                Our <span className="text-gold">Story</span>
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                Founded in 1999 by master jeweler Anna Rodriguez,Anni Jewelry began as a small workshop in Toronto with a simple mission: to create jewelry that captures the essence of love,beauty,and life's most treasured moments.
              </p>
              <p className="text-gray-300 text-lg mb-6">
                What started as a passion project has grown into one of Toronto's most trusted jewelry destinations. Our commitment to exceptional craftsmanship,personalized service,and timeless design has earned us the loyalty of thousands of customers.
              </p>
              <p className="text-gray-300 text-lg">
                Today,we continue to honor traditional jewelry-making techniques while embracing modern innovations,ensuring that every piece we create meets our exacting standards of quality and beauty.
              </p>
            </motion.div>
            <motion.div
              initial={{opacity: 0,x: 30}}
              whileInView={{opacity: 1,x: 0}}
              transition={{duration: 0.8}}
              viewport={{once: true}}
              className="relative"
            >
              <img src="https://i.ibb.co/hFQBnb5j/Anni-Jewelry-images-5.jpg" alt="Anni Jewelry Workshop" className="w-full h-96 object-cover rounded-lg shadow-gold" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-playfair">
              Our <span className="text-gold">Values</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              These core principles guide everything we do,from the initial design concept to the final presentation of your finished piece.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value,index)=> (
              <motion.div
                key={index}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                transition={{duration: 0.8,delay: index * 0.1}}
                viewport={{once: true}}
                className="text-center p-6 bg-black/50 rounded-lg border border-gold/20 hover:border-gold/50 transition-all duration-300"
              >
                <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-semibold text-gold mb-3">{value.title}</h3>
                <p className="text-gray-300">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-playfair">
              Meet Our <span className="text-gold">Team</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our talented team of artisans and professionals work together to bring your jewelry dreams to life with expertise and passion.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {team.map((member,index)=> (
              <motion.div
                key={index}
                initial={{opacity: 0,y: 30}}
                whileInView={{opacity: 1,y: 0}}
                transition={{duration: 0.8,delay: index * 0.1}}
                viewport={{once: true}}
                className="text-center bg-gray-900 p-6 rounded-lg border border-gold/20 hover:border-gold/50 transition-all duration-300"
              >
                <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-gold">
                  <img src={member.image} alt={member.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-xl font-bold text-gold mb-2">{member.name}</h3>
                <p className="text-gray-400 mb-4">{member.role}</p>
                <p className="text-gray-300">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-16 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-playfair">
              Our <span className="text-gold">Achievements</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {achievements.map((achievement,index)=> (
              <motion.div
                key={index}
                initial={{opacity: 0,scale: 0.5}}
                whileInView={{opacity: 1,scale: 1}}
                transition={{duration: 0.8,delay: index * 0.1}}
                viewport={{once: true}}
                className="text-center"
              >
                <div className="w-16 h-16 bg-gold rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={achievement.icon} className="w-8 h-8 text-black" />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-gold mb-2">{achievement.number}</div>
                <div className="text-gray-300">{achievement.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{opacity: 0,y: 30}}
            whileInView={{opacity: 1,y: 0}}
            transition={{duration: 0.8}}
            viewport={{once: true}}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-playfair">
              Our <span className="text-gold">Mission</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto mb-8">
              To create exceptional jewelry that celebrates life's most precious moments,combining traditional craftsmanship with contemporary design to produce pieces that will be treasured for generations to come.
            </p>
            <div className="bg-gray-900 p-8 rounded-lg border border-gold/20 max-w-3xl mx-auto">
              <p className="text-lg text-gold italic font-playfair">
                "Every piece we create is more than just jewelry – it's a symbol of love,a keeper of memories,and a work of art that tells your unique story."
              </p>
              <p className="text-gray-400 mt-4">- Anna Rodriguez,Founder</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;